import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "error",
    "loading",
    "loadingMessage",
    "success",
    "successMessage"
  ]

  static values = {
    endpoint: String,
    redirectUrl: String
  }

  connect() {
    this.confirmEmail()
  }

  async confirmEmail() {
    const token = this.confirmationToken()

    if (!token) {
      this.showError("This confirmation link is missing its token. Request a new confirmation email and try again.")
      return
    }

    this.showLoading("Confirming your email and preparing your account...")

    try {
      const response = await fetch(this.endpointValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin",
        body: JSON.stringify({
          confirmation: {
            confirmation_token: token
          }
        })
      })

      const body = await response.json().catch(() => ({}))

      if (!response.ok) {
        this.showError(this.errorMessage(body))
        return
      }

      this.persistAuthToken(body?.auth_token)
      this.showSuccess("Your email has been confirmed. Redirecting to your dashboard...")
      window.location.assign(body?.redirect_url || this.redirectUrlValue || "/")
    } catch (_error) {
      this.showError("We couldn't confirm this email right now. Please try again.")
    }
  }

  confirmationToken() {
    return new URLSearchParams(window.location.search).get("token") || ""
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  }

  persistAuthToken(token) {
    if (!token) return

    window.localStorage.setItem("authToken", token)
    window.localStorage.setItem("authTokenType", "Bearer")
  }

  showLoading(message) {
    this.loadingMessageTarget.textContent = message
    this.loadingTarget.classList.remove("hidden")
    this.successTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
  }

  showSuccess(message) {
    this.successMessageTarget.textContent = message
    this.loadingTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
    this.successTarget.classList.remove("hidden")
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.loadingTarget.classList.add("hidden")
    this.successTarget.classList.add("hidden")
    this.errorTarget.classList.remove("hidden")
  }

  errorMessage(body) {
    const details = body?.error?.details

    if (details && typeof details === "object") {
      const messages = Object.values(details).flat().filter(Boolean)

      if (messages.length > 0) {
        return messages.join(" ")
      }
    }

    return body?.error?.message || "Email confirmation could not be completed."
  }
}
